/**
 * MyApp — main application wrapper with splash screen.
 * Handles authentication flow, initialization, and route redirection.
 */
import { useEffect, useMemo, useState } from "react";
import { App, Spin } from "antd";
import { useIsAuthenticated, useIsSetting, useNotificationsEnabled, useSession } from "./context/SessionContext";
import { useNavigate, Outlet } from "react-router-dom";
import { Controller } from "./lib/Controller";
import { NewMessage } from "telegram/events";
import { getActivePage } from "./routes/AppRoutes";
import FacilitiesService from "./lib/services/FacilitiesService";

export const updateManager = new Map<string, (update: any, type: number) => void>();

function MyApp() {
  const { message } = App.useApp();
  const isSetting = useIsSetting();
  const session = useSession();
  const isNotificationsEnabled = useNotificationsEnabled();
  const isAuthenticated = useIsAuthenticated();
  const navigate = useNavigate();

  const [isInitialized, setIsInitialized] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  // Derive current page
  const activePage = useMemo(() => getActivePage(), []);

  /**
   * Wait until session context is ready
   */
  useEffect(() => {
    if (session === undefined || session === null) return;
    // Allow a small delay to keep splash screen visible
    const timer = setTimeout(() => setIsInitialized(true), 300);
    return () => clearTimeout(timer);
  }, [session]);

  /**
   * Handle Telegram updates (when authenticated)
   */
  useEffect(() => {
    if (!isAuthenticated) return;
    let disposeNew: undefined | (() => void);
    let disposeStatus: undefined | (() => void);

    (async () => {
      try {
        disposeNew = await Controller.tgApi.handleUpdates?.((update: any) => {
          updateManager.get(getActivePage())?.(update, 0);
        }, new NewMessage());

        disposeStatus = await Controller.tgApi.handleUpdates?.((update: any) => {
          if ((update.className as any) === "UpdateUserStatus") {
            updateManager.get(getActivePage())?.(update, 1);
          }
        });
      } catch (err) {
        console.error("[MyApp] Error initializing updates:", err);
      }
    })();

    return () => {
      try { disposeNew && disposeNew(); } catch {}
      try { disposeStatus && disposeStatus(); } catch {}
    };
  }, [isAuthenticated]);

  /**
   * Navigation logic after initialization
   */
  useEffect(() => {
    if (!isInitialized) return;

    // Add a fade transition for splash screen exit
    const timer = setTimeout(() => setShowSplash(false), 400);

    if (!isAuthenticated) {
      navigate("/login", { replace: true });
      return;
    }

    if (!isSetting) {
      navigate("/profile", { replace: true });
      return;
    }

    const currentPath = window.location.hash;
    if (currentPath.includes("#/chat/")) return;

    if (currentPath === "#/" || currentPath === "#") {
      navigate("/contacts", { replace: true });
    }

    return () => clearTimeout(timer);
  }, [isInitialized, isAuthenticated, isSetting, navigate]);

  /**
   * Enable push notifications after login
   */
  useEffect(() => {
    const askForEnable = async () => {
      if (!session || !session.session) return;
      try {
        return await FacilitiesService.enablePushNotifications({
          stringSession: localStorage.getItem("stringSession") as string,
        });
      } catch (err) {
        console.error("[MyApp] Error enabling push notifications:", err);
        return false;
      }
    };

    if (!isAuthenticated || !isInitialized) return;
    if (!isNotificationsEnabled) {
      askForEnable().then((enabled) => {
        if (enabled) {
          message.success("Notifiche push abilitate con successo!");
          setTimeout(() => window.location.reload(), 1000);
        }
      });
    }
  }, [isNotificationsEnabled, isAuthenticated, isInitialized, session, message]);

  /**
   * Splash screen (visible while loading everything)
   */
  if (showSplash) {
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          background: "linear-gradient(135deg, #007aff, #00b4d8)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          color: "white",
          transition: "opacity 0.4s ease",
        }}
      >
        <img
          src="/icon/logo_192x192.png"
          alt="App Icon"
          style={{
            width: 96,
            height: 96,
            marginBottom: 24,
            borderRadius: 24,
            boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
          }}
        />
        <h2 style={{ marginBottom: 12, fontWeight: 500, fontSize: "1.5rem" }}>
          Avvio dell'applicazione…
        </h2>
        <Spin size="large" tip="Caricamento dati..." style={{ color: "white" }} />
      </div>
    );
  }

  /**
   * Main App
   */
  return (
    <App>
      <Outlet />
    </App>
  );
}

export default MyApp;
